import { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { uploadImage, deleteImage } from "../../cloudinary";
import type { Product } from "../../types";
import { useCategories } from "../../hooks/useCategories";
import { slugify } from "../../utils/slugify";
import {
  Box,
  VStack,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useDisclosure,
  useToast,
  Image,
  IconButton,
  Spinner,
  HStack,
  Text,
  SimpleGrid,
  Tag,
  Flex,
  Divider,
} from "@chakra-ui/react";
import { DeleteIcon, AddIcon, EditIcon } from "@chakra-ui/icons";

const AVAILABILITY_OPTIONS = ["in-stock", "out-of-stock", "pre-order"];

const AdminProducts = () => {
  const { categories } = useCategories();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initialFormData = {
    name: "",
    description: "",
    price: "",
    category: "",
    subcategorySlug: "",
    brand: "",
    availability: "",
    specs: [] as Array<{ key: string; value: string }>,
    images: [] as Array<{ url: string; publicId: string }>,
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const snap = await getDocs(collection(db, "products"));
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        images: d.data().images || [],
      })) as Product[];
      setProducts(list);
    } catch {
      toast({ title: "Error fetching products", status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (formData.images.length >= 5) {
      toast({
        title: "Maximum images reached",
        description: "You can only upload up to 5 images per product",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsUploading(true);
      const { url, publicId } = await uploadImage(file);
      setFormData({ ...formData, images: [...formData.images, { url, publicId }] });
      toast({ title: "Image uploaded successfully", status: "success", duration: 3000, isClosable: true });
    } catch {
      toast({ title: "Error uploading image", status: "error", duration: 3000, isClosable: true });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const specs = formData.specs.reduce<Record<string, string>>((acc, { key, value }) => {
        if (key.trim()) acc[key.trim()] = value;
        return acc;
      }, {});

      const productData: Record<string, unknown> = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        categorySlug: slugify(formData.category),
        images: formData.images,
      };
      if (formData.subcategorySlug) productData.subcategorySlug = formData.subcategorySlug;
      if (formData.brand) productData.brand = formData.brand;
      if (formData.availability) productData.availability = formData.availability;
      if (Object.keys(specs).length > 0) productData.specs = specs;

      if (selectedProduct) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await updateDoc(doc(db, "products", selectedProduct.id), productData as any);
        toast({ title: "Product updated successfully", status: "success", duration: 3000, isClosable: true });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await addDoc(collection(db, "products"), productData as any);
        toast({ title: "Product added successfully", status: "success", duration: 3000, isClosable: true });
      }

      onClose();
      setFormData(initialFormData);
      setSelectedProduct(null);
      fetchProducts();
    } catch {
      toast({ title: "Error saving product", status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      subcategorySlug: product.subcategorySlug || "",
      brand: product.brand || "",
      availability: product.availability || "",
      specs: product.specs
        ? Object.entries(product.specs).map(([key, value]) => ({ key, value }))
        : [],
      images: product.images || [],
    });
    onOpen();
  };

  const handleDelete = async (productId: string, images: Array<{ publicId: string }>) => {
    try {
      await Promise.all(images.map((img) => deleteImage(img.publicId)));
      await deleteDoc(doc(db, "products", productId));
      toast({ title: "Product deleted successfully", status: "success", duration: 3000, isClosable: true });
      fetchProducts();
    } catch {
      toast({ title: "Error deleting product", status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleRemoveImage = async (publicId: string) => {
    try {
      await deleteImage(publicId);
      setFormData({ ...formData, images: formData.images.filter((img) => img.publicId !== publicId) });
      toast({ title: "Image removed successfully", status: "success", duration: 3000, isClosable: true });
    } catch {
      toast({ title: "Error removing image", status: "error", duration: 3000, isClosable: true });
    }
  };

  const handleAddNew = () => {
    setSelectedProduct(null);
    setFormData(initialFormData);
    onOpen();
  };

  const selectedCategory = categories.find((c) => c.name === formData.category);

  const updateSpec = (index: number, field: "key" | "value", value: string) => {
    const specs = [...formData.specs];
    specs[index] = { ...specs[index], [field]: value };
    setFormData({ ...formData, specs });
  };

  const removeSpec = (index: number) => {
    setFormData({ ...formData, specs: formData.specs.filter((_, i) => i !== index) });
  };

  return (
    <VStack spacing={6} align="stretch">
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Box>
          <Heading fontSize="22px" color="rs.text">
            Product Management
          </Heading>
          <Text fontSize="12.5px" color="rs.textDim" mt="2px">
            {products.length} product{products.length === 1 ? "" : "s"}
          </Text>
        </Box>
        <Button colorScheme="red" onClick={handleAddNew}>
          Add New Product
        </Button>
      </Flex>

      <Box
        bg="rs.surface"
        border="1px solid"
        borderColor="rs.border"
        borderRadius="10px"
        overflow="hidden"
      >
        <Table variant="unstyled" size="sm">
          <Thead bg="rs.bgAlt">
            <Tr>
              <Th
                fontSize="10px"
                fontWeight="600"
                letterSpacing="0.08em"
                color="rs.textDim"
                py={3}
                pl={5}
              >
                Product
              </Th>
              <Th fontSize="10px" fontWeight="600" letterSpacing="0.08em" color="rs.textDim" py={3}>
                Category
              </Th>
              <Th fontSize="10px" fontWeight="600" letterSpacing="0.08em" color="rs.textDim" py={3} isNumeric>
                Price
              </Th>
              <Th fontSize="10px" fontWeight="600" letterSpacing="0.08em" color="rs.textDim" py={3} pr={5}>
                Actions
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {products.map((product, i) => (
              <Tr
                key={product.id}
                borderTop={i === 0 ? "none" : "1px solid"}
                borderColor="rs.border"
                _hover={{ bg: "rs.surfaceHover" }}
                transition="background 0.12s"
              >
                <Td py={3} pl={5}>
                  <HStack spacing={3} align="center">
                    <Box position="relative" flexShrink={0}>
                      <Image
                        src={product.images[0]?.url}
                        alt={product.name}
                        boxSize="48px"
                        objectFit="cover"
                        borderRadius="8px"
                        bg="rs.imageWell"
                        border="1px solid"
                        borderColor="rs.border"
                        fallback={
                          <Box
                            boxSize="48px"
                            borderRadius="8px"
                            bg="rs.imageWell"
                            border="1px solid"
                            borderColor="rs.border"
                          />
                        }
                      />
                      {product.images.length > 1 && (
                        <Flex
                          position="absolute"
                          bottom="-4px"
                          right="-4px"
                          boxSize="18px"
                          borderRadius="full"
                          bg="rs.red"
                          color="white"
                          align="center"
                          justify="center"
                          fontSize="9px"
                          fontWeight="600"
                          border="2px solid"
                          borderColor="rs.surface"
                        >
                          {product.images.length}
                        </Flex>
                      )}
                    </Box>
                    <Text fontSize="13.5px" fontWeight="500" color="rs.text" noOfLines={1}>
                      {product.name}
                    </Text>
                  </HStack>
                </Td>
                <Td py={3}>
                  <Tag size="sm" colorScheme="red" variant="subtle" fontSize="11px">
                    {product.category}
                  </Tag>
                </Td>
                <Td py={3} isNumeric whiteSpace="nowrap">
                  <Text fontSize="13.5px" fontWeight="500" color="rs.text">
                    {product.price.toLocaleString("en-LK", { maximumFractionDigits: 2 })}
                  </Text>
                  <Text fontSize="10px" color="rs.textDim">
                    LKR
                  </Text>
                </Td>
                <Td py={3} pr={5}>
                  <HStack spacing={2} justify="flex-end">
                    <IconButton
                      aria-label="Edit product"
                      icon={<EditIcon />}
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(product)}
                    />
                    <IconButton
                      aria-label="Delete product"
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="outline"
                      color="rs.red"
                      borderColor="rs.redBorder"
                      _hover={{ bg: "rs.red", color: "white", borderColor: "rs.red" }}
                      onClick={() => handleDelete(product.id, product.images)}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {products.length === 0 && (
          <>
            <Divider borderColor="rs.border" />
            <Flex py={10} align="center" justify="center">
              <Text fontSize="13px" color="rs.textMuted">
                No products yet — add your first one above.
              </Text>
            </Flex>
          </>
        )}
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selectedProduct ? "Edit Product" : "Add New Product"}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Product Images ({formData.images.length}/5)</FormLabel>
                  <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                    {formData.images.map((image) => (
                      <Box key={image.publicId} position="relative">
                        <Image src={image.url} alt="Product" width="100%" height="100px" objectFit="cover" borderRadius="md" />
                        <IconButton
                          aria-label="Remove image"
                          icon={<DeleteIcon />}
                          size="sm"
                          position="absolute"
                          top={2}
                          right={2}
                          onClick={() => handleRemoveImage(image.publicId)}
                        />
                      </Box>
                    ))}
                    {formData.images.length < 5 && (
                      <Box
                        borderWidth={2}
                        borderStyle="dashed"
                        borderRadius="md"
                        height="100px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {isUploading ? (
                          <Spinner />
                        ) : (
                          <VStack spacing={1}>
                            <AddIcon />
                            <Text fontSize="sm">Add Image</Text>
                          </VStack>
                        )}
                      </Box>
                    )}
                  </SimpleGrid>
                  <Input type="file" accept="image/*" onChange={handleImageUpload} ref={fileInputRef} display="none" />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Category</FormLabel>
                  <Select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value, subcategorySlug: "" })}
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {selectedCategory && selectedCategory.subcategories.length > 0 && (
                  <FormControl>
                    <FormLabel>Subcategory</FormLabel>
                    <Select
                      value={formData.subcategorySlug}
                      onChange={(e) => setFormData({ ...formData, subcategorySlug: e.target.value })}
                    >
                      <option value="">None</option>
                      {selectedCategory.subcategories.map((sub) => (
                        <option key={sub.slug} value={sub.slug}>
                          {sub.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <FormControl isRequired>
                  <FormLabel>Price (LKR)</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Brand</FormLabel>
                  <Input value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} />
                </FormControl>

                <FormControl>
                  <FormLabel>Availability</FormLabel>
                  <Select
                    value={formData.availability}
                    onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                  >
                    <option value="">Not set</option>
                    {AVAILABILITY_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Specs (used to power filters)</FormLabel>
                  <VStack align="stretch" spacing={2}>
                    {formData.specs.map((spec, i) => (
                      <HStack key={i}>
                        <Input
                          placeholder="Key (e.g. Sensor)"
                          value={spec.key}
                          onChange={(e) => updateSpec(i, "key", e.target.value)}
                        />
                        <Input
                          placeholder="Value (e.g. Full Frame)"
                          value={spec.value}
                          onChange={(e) => updateSpec(i, "value", e.target.value)}
                        />
                        <IconButton aria-label="Remove spec" icon={<DeleteIcon />} size="sm" onClick={() => removeSpec(i)} />
                      </HStack>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setFormData({ ...formData, specs: [...formData.specs, { key: "", value: "" }] })
                      }
                    >
                      Add spec
                    </Button>
                  </VStack>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="red">
                {selectedProduct ? "Update" : "Add"} Product
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default AdminProducts;
